import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PLAYBOOK_CATEGORIES, PLAYBOOK_POSTS } from '../data/playbook.js';
import { docBaiDaXuatBan } from '../lib/playbook.js';
import { categoriesOfTrack, categoryBySlug, categorySlug, TRACKS } from '../lib/playbookModel.js';
import { StoryBadge } from '../components/PlaybookParts.jsx';
import { cacTram, tramCuaBai, SO_TRAM } from '../data/lo-trinh.js';
import PlaybookKe from '../components/PlaybookKe.jsx';
import { mauCuaTram } from '../data/mauTram.js';
import { docChon, ghiChon, locVaDem } from '@shared/locMuc.js';
import { KhungHaiCot, NhomLoc, OTim, DongDem } from '../components/LocHaiCot.jsx';

// Nhóm chính của Playbook là 5 trạm của Lộ trình, để người đọc không phải
// học thêm một cách phân loại. Bài chưa gắn trạm rơi vào nhóm "Khác".
const KHAC = 'khac';
const tramCuaPost = (post) => tramCuaBai(post.slug)?.id || KHAC;

/** Thẻ bài cùng kiểu thẻ công cụ (/cong-cu): ảnh bìa, nhãn trạm, tên, tóm tắt, số phút. */
function Card({ post, featured = false }) {
  const tram = tramCuaBai(post.slug);
  const mau = tram && tram.so > 0 ? mauCuaTram(tram) : '#a9bccd';
  return <Link to={`/playbook/${post.slug}`} className="cct pbc" style={{ '--mau': mau }}>
    <span className="pbc-anh">
      {post.coverImage
        ? <img src={post.coverImage} alt="" loading={featured ? 'eager' : 'lazy'} decoding="async" />
        : <span className={`playbook-cover-empty pb-cat-${categorySlug(post.category)}`}><span>{post.category}</span></span>}
    </span>
    <span className="pbc-than">
      <span className="pbc-nhan">
        {featured && <StoryBadge type={post.storyType} />}
        {tram && tram.so > 0 && <span className="pbc-tram"><i aria-hidden="true" />Trạm {tram.so}</span>}
        <span>{post.category}</span>
      </span>
      <span className="cct-ten">{post.title}</span>
      <span className="cct-hoi pbc-tom-tat">{post.excerpt}</span>
      <span className="cct-chan">
        <span>{post.readingMinutes} phút đọc</span>
        <span className="cct-dung">Đọc bài →</span>
      </span>
    </span>
  </Link>;
}

export default function Playbook() {
  const { cat } = useParams();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const trackSlug = Object.keys(TRACKS).find((t) => pathname.replace(/\/$/, '') === `/playbook/${t}`) || null;
  const track = trackSlug ? TRACKS[trackSlug] : null;
  const category = cat ? categoryBySlug(cat) : null;
  const tramChon = docChon(params, 'tram');
  const query = params.get('q') || '';
  const setQuery = (v) => setParams(ghiChon(params, 'q', v ? [v] : []), { replace: true });
  const [posts, setPosts] = useState(PLAYBOOK_POSTS);
  const dsBai = useRef(null);
  const navigate = useNavigate();
  useEffect(() => { docBaiDaXuatBan().then(setPosts); }, []);

  const khopChu = (post) => `${post.title} ${post.excerpt} ${post.category} ${(post.stages || []).join(' ')}`.toLowerCase().includes(query.trim().toLowerCase());
  // Dạng bài và chủ đề là TRANG riêng (/playbook/cam-nang, /playbook/chu-de/...),
  // nên lọc bằng đường dẫn; trạm là ô chọn nhiều, nằm trên ?tram=.
  const trongPhamVi = useMemo(() => posts.filter((post) =>
    (!trackSlug || post.track === trackSlug) && (!category || post.category === category) && khopChu(post),
  ), [posts, query, category, trackSlug]); // eslint-disable-line react-hooks/exhaustive-deps
  const { ketQua: filtered, dem: demTram } = locVaDem(trongPhamVi, [{ key: 'tram', giaTri: (p) => [tramCuaPost(p)] }], { tram: tramChon });
  const dem = (id) => demTram.tram[id] || 0;
  const hopTram = (p) => !tramChon.length || tramChon.includes(tramCuaPost(p));
  const demDang = (slug) => posts.filter((p) => (!slug || p.track === slug) && (!category || p.category === category) && khopChu(p) && hopTram(p)).length;
  const demChuDe = (c) => posts.filter((p) => (!trackSlug || p.track === trackSlug) && (!c || p.category === c) && khopChu(p) && hopTram(p)).length;
  const tramLoc = tramChon.length === 1 ? tramChon[0] : '';
  const chonTram = (id) => setParams(ghiChon(params, 'tram', id ? [id] : []), { replace: true });
  const giuQuery = params.toString() ? `?${params.toString()}` : '';

  if (cat && !category) return <section className="surface section-space empty-state"><h1>Không có chủ đề này</h1><Link className="nut" to="/playbook">Về Playbook</Link></section>;

  const dangLoc = Boolean(category || track || tramChon.length || query.trim());
  const featured = dangLoc ? null : (filtered.find((post) => post.featured) || filtered[0]);
  const rest = filtered.filter((post) => post !== featured);
  // Ẩn chủ đề chưa có bài (ví dụ Startup Spotlight trước bài đầu tiên), trừ chủ đề đang xem.
  const cacChuDe = (trackSlug ? categoriesOfTrack(trackSlug) : PLAYBOOK_CATEGORIES)
    .filter((c) => c === category || posts.some((p) => p.category === c));
  const cacNhom = [
    ...cacTram().map((t) => ({ id: t.id, so: t.so, ten: t.ten, mau: mauCuaTram(t), posts: trongPhamVi.filter((p) => tramCuaPost(p) === t.id) })),
    ...(dem(KHAC) ? [{ id: KHAC, so: 0, ten: 'Khác', mau: '#a9bccd', posts: trongPhamVi.filter((p) => tramCuaPost(p) === KHAC) }] : []),
  ];
  const tenTramLoc = tramLoc === KHAC ? 'Khác' : cacTram().find((t) => t.id === tramLoc)?.ten;

  return <>
    {/* Đầu trang: lời giới thiệu + "kệ sách" vẽ từ bài thật; chạm một chồng
        sách là lọc theo trạm (thay hàng nút lọc trạm trước đây). */}
    <div className="pb-dau">
      <header className="playbook-heading">
        <p className="eyebrow">{category || track ? <><Link to="/playbook">Playbook</Link> / {category ? 'Chủ đề' : 'Loạt bài'}</> : 'PLAYBOOK'}</p>
        <h1>{category || track?.label || 'Cẩm nang gọi vốn'}</h1>
        <p className="lead">{category
          ? `Các bài về ${category.toLowerCase()} dành cho founder gọi vốn lần đầu.`
          : track ? track.lead
          : 'Hướng dẫn, tình huống và mẫu giúp bạn làm từng việc khi chuẩn bị gọi vốn.'}</p>
        <p className="playbook-lo-trinh">Lần đầu chuẩn bị gọi vốn? <Link to="/lo-trinh">Xem lộ trình {SO_TRAM} trạm →</Link></p>
      </header>
      <PlaybookKe
        nhom={cacNhom}
        dangChon={tramLoc}
        onChon={(id) => {
          chonTram(id);
          if (id) requestAnimationFrame(() => dsBai.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        }}
      />
    </div>

    <div ref={dsBai}>
      <KhungHaiCot
        soDangChon={tramChon.length + (category ? 1 : 0) + (track ? 1 : 0)}
        boLoc={<>
          <NhomLoc tieuDe="Loạt bài" chon={trackSlug ? [trackSlug] : []} tatCa="Mọi loạt bài" tatCaTo={`/playbook${giuQuery}`} tatCaDem={demDang('')}
            ds={Object.values(TRACKS).filter((t) => t.slug === trackSlug || posts.some((p) => p.track === t.slug)).map((t) => ({ v: t.slug, nhan: t.label, to: `/playbook/${t.slug}${giuQuery}`, dem: demDang(t.slug) }))} />
          {/* Chủ đề hiển thị theo loạt đang chọn, nên đặt ngay dưới Loạt bài. */}
          <NhomLoc tieuDe="Chủ đề" chon={category ? [category] : []} tatCa="Mọi chủ đề" tatCaTo={`/playbook${trackSlug ? `/${trackSlug}` : ''}${giuQuery}`} tatCaDem={demChuDe('')}
            ds={cacChuDe.map((c) => ({ v: c, nhan: c, to: `/playbook/chu-de/${categorySlug(c)}${giuQuery}`, dem: demChuDe(c) }))} />
          <NhomLoc tieuDe="Trạm trong lộ trình" chon={tramChon} onDoi={(ds) => setParams(ghiChon(params, 'tram', ds), { replace: true })}
            ds={cacNhom.map((n) => ({ v: n.id, nhan: n.so ? `Trạm ${n.so}: ${n.ten}` : 'Khác', dem: dem(n.id), mau: n.mau }))} />
        </>}
      >
        <OTim id="playbook-search" nhan="Tìm bài" value={query} onChange={setQuery} placeholder="Tìm: số vốn cần gọi, pitch deck, email…" />
        <DongDem dangLoc={dangLoc} onXoa={() => { setParams({}, { replace: true }); if (category || track) navigate('/playbook'); }}>
          {tenTramLoc && tramLoc !== KHAC
            ? <><b>{filtered.length} bài</b>: Trạm {cacTram().find((t) => t.id === tramLoc)?.so}, {tenTramLoc}</>
            : <>Hiển thị <b>{filtered.length}</b> bài{category ? ` về ${category.toLowerCase()}` : track ? `, ${track.label.toLowerCase()}` : ''}</>}
        </DongDem>
        {filtered.length ? <div className="cc-luoi pbc-luoi">
          {featured && <Card post={featured} featured />}
          {rest.map((post) => <Card post={post} key={post.slug} />)}
        </div> : <section className="surface empty-state"><h2>Chưa tìm thấy bài phù hợp</h2><p>Thử từ khoá ngắn hơn, như “runway”, “cap table” hay “pitch deck”, hoặc bỏ lọc trạm.</p><button type="button" className="nut phu" onClick={() => { setParams({}, { replace: true }); }}>Xoá lọc</button></section>}
      </KhungHaiCot>
    </div>

    <aside className="pb-share-story section-space" aria-labelledby="gop-chuyen">
      <div>
        <p className="eyebrow">Góp một câu chuyện</p>
        <h2 id="gop-chuyen">Bạn từng gọi vốn, thành hay chưa thành?</h2>
        <p>Một lần bị từ chối, một câu hỏi khó của nhà đầu tư, một quyết định chia cổ phần… đều có thể giúp founder khác đi nhanh hơn. Bạn có thể ẩn danh; bài chỉ được đăng khi bạn đồng ý bản cuối.</p>
      </div>
      <Link className="nut" to="/playbook/gop-cau-chuyen">Gửi câu chuyện của bạn</Link>
    </aside>
  </>;
}
